import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const inPromptHeaderStyles = cssMap({
	default: {
		display: 'inline-flex',
		// make room for the focus ring
		marginRight: token('space.050'),
	},
});

type Props = {
	// if true, the button will be styled (move up a bit) to match the prompt header
	inPromptHeader?: boolean;
	isDisabled?: boolean;
	onClick?: () => void;
	stopPropagation?: boolean;
};

const toolTipProps: React.ComponentProps<typeof IconButton>['tooltip'] = { position: 'top' };

export const BackIconButton = ({
	inPromptHeader,
	isDisabled = false,
	stopPropagation,
	onClick,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const onClickIconButton = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			if (stopPropagation) {
				event.stopPropagation();
			}
			onClick?.();
		},
		[stopPropagation, onClick],
	);

	const btn = (
		<IconButton
			isDisabled={isDisabled}
			appearance="subtle"
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			icon={(iconProps) => (
				<ChevronLeftIcon label={iconProps.label} testId={iconProps.testId} size="small" />
			)}
			label={formatMessage(messages.backLabel)}
			spacing="compact"
			isTooltipDisabled={isDisabled}
			onClick={onClickIconButton}
			testId="back-icon-button"
			tooltip={toolTipProps}
		/>
	);

	if (inPromptHeader) {
		return <Flex xcss={inPromptHeaderStyles.default}>{btn}</Flex>;
	}

	return btn;
};
